/**
 * @fileoverview The photo dialog, which is two dialogs wearing one name.
 *
 * This file was a thousand lines holding a camera, a Google Photos client, an
 * EXIF reader, a note-persistence policy, and two entirely different screens
 * separated by `editMode ?`. Uploading a picture and editing one share almost
 * nothing but a plant, so they are now two components; what they genuinely
 * share lives in ./photo as hooks and fields either can use.
 *
 * This shim stays because a card asks for one dialog and passes editMode, and
 * changing that is a separate argument from unpicking the inside.
 *
 * Where: PlantCard, LocationCard and ShareMoments ask for it; it opens
 * EditPhotoDialog with editMode, UploadPhotoDialog without.
 * A photo added, changed or deleted here marks the season brief stale
 * (seasonWritten, W24 Job H), so the diary on Seasons shows it at once.
 * API: none of its own (the two dialogs').
 * Settings: none.
 * Words (en/es): none of its own.
 * Watch out: userId and plantObjId go through untouched; with a location
 * subject, plantObjId is the bed's id.
 *
 * @module PhotoUploadDialog
 */

import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PlantPhoto } from '@/shared/types/photo';
import type { Subject } from '@/shared/api/subject';
import { UploadPhotoDialog } from './photo/UploadPhotoDialog';
import { EditPhotoDialog } from './photo/EditPhotoDialog';
import { seasonWritten } from '@/shared/lib/briefFreshness';

/**
 * What a card hands the photo dialog: both screens' props in one, with
 * editMode saying which screen they are for.
 */
interface PhotoUploadDialogProps {
  /** Whether the dialog is currently open */
  isOpen: boolean;
  /** Callback function to close the dialog */
  onClose: () => void;
  /** ID of the user who owns the plant */
  userId: number;
  /** ID of the plant object to associate the photo with */
  plantObjId: number;
  /** A location's photo instead of a plant's. */
  subject?: Subject;
  /** Callback function called when a photo is successfully uploaded or updated */
  onPhotoUploaded: (photo: PlantPhoto) => void;
  /** Whether the dialog is in edit mode (true) or upload mode (false) */
  editMode?: boolean;
  /** Existing photo data when in edit mode */
  existingPhoto?: PlantPhoto;
  /** Callback function called when a photo is deleted */
  onPhotoDeleted?: (photoId: number) => void;
}

/**
 * Opens whichever of the two photo dialogs the caller meant.
 *
 * @param props - PhotoUploadDialogProps.
 * @component
 * @example
 * ```tsx
 * <PhotoUploadDialog
 *   isOpen={true}
 *   onClose={() => setIsOpen(false)}
 *   userId={1}
 *   plantObjId={123}
 *   onPhotoUploaded={(photo) => console.log('Photo uploaded:', photo)}
 * />
 * ```
 */
export const PhotoUploadDialog: React.FC<PhotoUploadDialogProps> = ({
  isOpen,
  onClose,
  userId,
  plantObjId,
  subject,
  onPhotoUploaded,
  editMode,
  existingPhoto,
  onPhotoDeleted,
}) => {
  const queryClient = useQueryClient();
  /**
   * A photo uploaded or changed: the caller hears of it, and the season diary is marked stale.
   *
   * @param photo - The photo as saved.
   */
  const uploaded = (photo: PlantPhoto): void => {
    seasonWritten(queryClient);
    onPhotoUploaded(photo);
  };
  /**
   * A photo deleted: the caller hears of it, and the season diary is marked stale.
   *
   * @param photoId - The photo deleted.
   */
  const deleted = (photoId: number): void => {
    seasonWritten(queryClient);
    onPhotoDeleted?.(photoId);
  };
  return editMode ? (
    <EditPhotoDialog
      isOpen={isOpen}
      onClose={onClose}
      userId={userId}
      plantObjId={plantObjId}
      subject={subject}
      photo={existingPhoto}
      onPhotoUpdated={uploaded}
      onPhotoDeleted={deleted}
    />
  ) : (
    <UploadPhotoDialog
      isOpen={isOpen}
      onClose={onClose}
      plantObjId={plantObjId}
      subject={subject}
      onPhotoUploaded={uploaded}
    />
  );
};
